
import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import {
    Card,
    CardBody,
    Col,
    Form,
    Input,
    Label,
    Row,
    FormFeedback,
    Spinner,
    Container
} from "reactstrap";
import { Btn, H5 } from "../../AbstractElements";
import Breadcrumbs from "../../CommonElements/Breadcrumbs";
import { apiPost, uploadFile, mediaUrl } from "../../Utils";
import { useNavigate, useLocation } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useAuth } from "../../Context/AuthContext";
import "react-phone-input-2/lib/style.css";
import PhoneInput from "react-phone-input-2";
import { parsePhoneNumberFromString, CountryCode } from "libphonenumber-js";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

interface FormValues {
    name: string;
    email: string;
    phone?: string;
    address?: string;
    designation?: string;
    joining_date?: string;
    status?: string;
    password?: string;
    role_id: string;
    profile_image: FileList;
}

export default function EditEmployee() {
    const navigate = useNavigate();
    const location = useLocation();
    const [loading, setLoading] = useState(true);
    const [roles, setRoles] = useState<any[]>([]);
    const [isPasswordVisible, setPasswordVisible] = useState(false);
    const togglePasswordVisibility = () => setPasswordVisible(!isPasswordVisible);
    const [preview, setPreview] = useState<string | null>(null);
    const { hasPermission } = useAuth();
    const {
        control,
        handleSubmit,
        setValue,
        formState: { errors },
    } = useForm<FormValues>();

    useEffect(() => {
        const init = async () => {
            await fetchRoles();
            if (location.state && location.state.id) {
                await fetchEmployeeDetails(location.state.id);
            } else {
                toast.error("No Employee ID provided");
                navigate(`${process.env.PUBLIC_URL}/employee`);
            }
        };
        init();
    }, [location.state, navigate]);

    const fetchRoles = async () => {
        try {
            const response: any = await apiPost("roles/list", {});
            const filteredRoles = Array.isArray(response)
                ? response.filter((role: any) => role.name !== 'Admin')
                : response;
            setRoles(filteredRoles);
        } catch (error) {
            console.error(error);
        }
    };

    const fetchEmployeeDetails = async (id: number) => {
        try {
            const response: any = await apiPost("users/viewbyid", { id: id });
            setValue("name", response.name);
            setValue("email", response.email);
            setValue("phone", response.phone);
            setValue("address", response.address);
            setValue("designation", response.designation);
            setValue("joining_date", response.joining_date);
            setValue("status", response.status);
            setValue("role_id", response.role_id);
            if (response.profile_image) {
                setPreview(`${mediaUrl}${response.profile_image}`);
            }
            // Password intentionally left blank
            setLoading(false);
        } catch (error) {
            toast.error("Failed to load employee details");
            setLoading(false);
        }
    };

    const validatePhoneNumber = (value: string | undefined) => {
        if (!value) {
            return "Phone number is required.";
        }
        const sanitizedValue = value.replace(/\D/g, "");
        const phoneNumber = parsePhoneNumberFromString(`+${sanitizedValue}`);
        if (!phoneNumber || !phoneNumber.isValid()) {
            return "Invalid phone number.";
        }
        const allowedCountries: CountryCode[] = ["IN", "SA", "AE", "QA", "KW", "OM", "BH"];
        if (!allowedCountries.includes(phoneNumber.country as CountryCode)) {
            return "Only GCC or Indian numbers are allowed.";
        }
        return true;
    };

    const onSubmit = async (data: FormValues) => {
        setLoading(true);
        try {
            let profileImageName = "";
            if (data.profile_image && data.profile_image.length > 0) {
                profileImageName = await uploadFile(data.profile_image[0]);
            }

            const payload: any = {
                id: location.state.id,
                name: data.name,
                email: data.email,
                phone: data.phone,
                address: data.address,
                designation: data.designation,
                joining_date: data.joining_date,
                status: data.status,
                role_id: data.role_id
            };
            if (data.password) {
                payload.password = data.password;
            }
            if (profileImageName) {
                payload.profile_image = profileImageName;
            }

            await apiPost("users/update", payload);
            toast.success("Employee updated successfully", { autoClose: 1000 });
            setTimeout(() => {
                navigate(`${process.env.PUBLIC_URL}/employee`);
            }, 1000);
        } catch (error: any) {
            toast.error(error.response?.data?.message || "Error updating employee");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="page-body">
            <Breadcrumbs pageTitle="Edit Employee" parent="Master Settings" title="Edit Employee" />
            {loading ? <div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: '70vh' }}><Spinner color="primary" /></div> : (
                <Container fluid>
                    <Card>
                        <CardBody>
                            {loading ? <div className="d-flex justify-content-center py-5"><Spinner /></div> : (
                                <Form onSubmit={handleSubmit(onSubmit)}>
                                    <Row className="g-3">
                                        <Col md={6}>
                                            <Label>Name <span className="text-danger">*</span></Label>
                                            <Controller
                                                name="name"
                                                control={control}
                                                rules={{ required: "Name is required" }}
                                                render={({ field }) => (
                                                    <>
                                                        <Input {...field} placeholder="Full Name" invalid={!!errors.name} autoComplete="off" />
                                                        <FormFeedback>{errors.name?.message}</FormFeedback>
                                                    </>
                                                )}
                                            />
                                        </Col>
                                        <Col md={6}>
                                            <Label>Email <span className="text-danger">*</span></Label>
                                            <Controller
                                                name="email"
                                                control={control}
                                                rules={{
                                                    required: "Email is required",
                                                    pattern: {
                                                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                                        message: "Invalid email address"
                                                    }
                                                }}
                                                render={({ field }) => (
                                                    <>
                                                        <Input {...field} placeholder="Email Address" invalid={!!errors.email} autoComplete="off" />
                                                        <FormFeedback>{errors.email?.message}</FormFeedback>
                                                    </>
                                                )}
                                            />
                                        </Col>
                                        <Col md={6}>
                                            <Label>Phone <span className="text-danger">*</span></Label>
                                            <Controller
                                                name="phone"
                                                control={control}
                                                rules={{
                                                    required: "Phone is required",
                                                    validate: validatePhoneNumber
                                                }}
                                                render={({ field }) => (
                                                    <>
                                                        <PhoneInput
                                                            country={"ae"}
                                                            onlyCountries={["ae", "sa", "in"]}
                                                            preferredCountries={["ae"]}
                                                            value={field.value}
                                                            onChange={(value) => field.onChange(value)}
                                                            inputProps={{
                                                                name: "phone",
                                                                placeholder: "Enter Phone",
                                                                autoComplete: "none"
                                                            }}
                                                            inputStyle={{
                                                                width: '100%',
                                                                borderColor: errors.phone ? 'red' : '#ced4da'
                                                            }}
                                                        />
                                                        <FormFeedback style={{ display: errors.phone ? 'block' : 'none' }}>{errors.phone?.message}</FormFeedback>
                                                    </>
                                                )}
                                            />
                                        </Col>
                                        <Col md={6}>
                                            <Label>Address <span className="text-danger">*</span></Label>
                                            <Controller
                                                name="address"
                                                control={control}
                                                rules={{ required: "Address is required" }}
                                                render={({ field }) => (
                                                    <>
                                                        <Input type="textarea" {...field} placeholder="Address" invalid={!!errors.address} autoComplete="off" />
                                                        <FormFeedback>{errors.address?.message}</FormFeedback>
                                                    </>
                                                )}
                                            />
                                        </Col>
                                        <Col md={6}>
                                            <Label>Designation <span className="text-danger">*</span></Label>
                                            <Controller
                                                name="designation"
                                                control={control}
                                                rules={{ required: "Designation is required" }}
                                                render={({ field }) => (
                                                    <>
                                                        <Input {...field} placeholder="Designation" invalid={!!errors.designation} autoComplete="off" />
                                                        <FormFeedback>{errors.designation?.message}</FormFeedback>
                                                    </>
                                                )}
                                            />
                                        </Col>
                                        <Col md={6}>
                                            <Label>Joining Date <span className="text-danger">*</span></Label>
                                            <Controller
                                                name="joining_date"
                                                control={control}
                                                rules={{ required: "Joining Date is required" }}
                                                render={({ field }) => (
                                                    <>
                                                        <DatePicker
                                                            className={`form-control ${errors.joining_date ? "is-invalid" : ""}`}
                                                            selected={field.value ? new Date(field.value) : null}
                                                            onChange={(date) => field.onChange(date ? formatDate(date) : "")}
                                                            placeholderText="Select Joining Date"
                                                            dateFormat="yyyy-MM-dd"
                                                            maxDate={new Date()}
                                                            peekNextMonth
                                                            showMonthDropdown
                                                            showYearDropdown
                                                            dropdownMode="select"
                                                            autoComplete="off"
                                                        />
                                                        <FormFeedback style={{ display: errors.joining_date ? 'block' : 'none' }}>{errors.joining_date?.message}</FormFeedback>
                                                    </>
                                                )}
                                            />
                                        </Col>
                                        <Col md={6}>
                                            <Label>Status <span className="text-danger">*</span></Label>
                                            <Controller
                                                name="status"
                                                control={control}
                                                rules={{ required: "Status is required" }}
                                                defaultValue="Active"
                                                render={({ field }) => (
                                                    <>
                                                        <Input type="select" {...field} invalid={!!errors.status}>
                                                            <option value="Active">Active</option>
                                                            <option value="Inactive">Inactive</option>
                                                        </Input>
                                                        <FormFeedback>{errors.status?.message}</FormFeedback>
                                                    </>
                                                )}
                                            />
                                        </Col>
                                        <Col md={6}>
                                            <Label>Profile Image</Label>
                                            <Controller
                                                name="profile_image"
                                                control={control}
                                                rules={{
                                                    validate: (files: any) => {
                                                        if (files && files[0] && files[0].size > 2 * 1024 * 1024) {
                                                            return "File size must be less than 2MB";
                                                        }
                                                        return true;
                                                    }
                                                }}
                                                render={({ field: { value, onChange, ...field } }) => (
                                                    <>
                                                        <Input
                                                            type="file"
                                                            {...field}
                                                            onChange={(e) => {
                                                                if (e.target.files && e.target.files.length > 0) {
                                                                    onChange(e.target.files);
                                                                }
                                                            }}
                                                            accept="image/*"
                                                            invalid={!!errors.profile_image}
                                                        />
                                                        {preview && !(value instanceof FileList && value.length > 0) && (
                                                            <a href={preview} target="_blank" rel="noreferrer" className="d-block mt-2">View Current Document</a>
                                                        )}
                                                        {value instanceof FileList && value.length > 0 && (
                                                            <a href={URL.createObjectURL(value[0])} target="_blank" rel="noreferrer" className="d-block mt-2">View New Document</a>
                                                        )}
                                                        <FormFeedback>{errors.profile_image?.message}</FormFeedback>
                                                    </>
                                                )}
                                            />
                                        </Col>
                                        <Col md={6}>
                                            <Label>Role <span className="text-danger">*</span></Label>
                                            <Controller
                                                name="role_id"
                                                control={control}
                                                rules={{ required: "Role is required" }}
                                                render={({ field }) => (
                                                    <>
                                                        <Input type="select" {...field} invalid={!!errors.role_id}>
                                                            <option value="">Select Role</option>
                                                            {roles.map((r: any) => (
                                                                <option key={r.id} value={r.id}>{r.name}</option>
                                                            ))}
                                                        </Input>
                                                        <FormFeedback>{errors.role_id?.message}</FormFeedback>
                                                    </>
                                                )}
                                            />
                                        </Col>
                                        <Col md={6}>
                                            <Label>Password</Label>
                                            <Controller
                                                name="password"
                                                control={control}
                                                rules={{ minLength: { value: 6, message: "Min 6 chars" } }}
                                                render={({ field }) => (
                                                    <>
                                                        <div className="form-input position-relative">
                                                            <Input type={isPasswordVisible ? "text" : "password"} {...field} placeholder="New Password" invalid={!!errors.password} autoComplete="off" />
                                                            <div className="show-hide" onClick={togglePasswordVisibility}>
                                                                <span className={!isPasswordVisible ? "show" : ""}></span>
                                                            </div>
                                                        </div>
                                                        <FormFeedback>{errors.password?.message}</FormFeedback>
                                                        <small className="text-muted">Leave blank to retain the existing password.</small>
                                                    </>
                                                )}
                                            />
                                        </Col>
                                    </Row>
                                    <div className="mt-4">
                                        <Btn color="primary" disabled={loading} type="submit">
                                            {loading ? <>Updating <Spinner size="sm" /></> : "Update"}
                                        </Btn>
                                    </div>
                                </Form>
                            )}
                        </CardBody>
                    </Card>
                </Container>
            )}
            <ToastContainer />
        </div>
    );
}
